import { useEffect, useState } from "react";
import { Copy, KeyRound, Send, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function CopyValue({ label, testId, value }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }
  return (
    <div className="secret-row">
      <strong>{label}</strong>
      <code data-testid={testId}>{value}</code>
      <button
        className="button button-secondary"
        data-testid={`${testId}-copy`}
        type="button"
        onClick={copy}
      >
        <Copy size={14} /> {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export const ProviderPortalAccessPanel = ({ provider, onClose, onGranted }) => {
  const [email, setEmail] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [issueTemp, setIssueTemp] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const providerId = provider?.id;
  useEffect(() => {
    if (!provider) {
      return;
    }
    const known = provider.email && !provider.email.endsWith("@pending.invalid") ? provider.email : "";
    setEmail(known);
    setResult(null);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  if (!provider) {
    return null;
  }

  async function submit(event) {
    event.preventDefault();
    setIsSaving(true);
    try {
      const response = await api.post(`/providers/${provider.id}/portal-access`, {
        email,
        send_invite_email: sendInvite,
        issue_temporary_password: issueTemp,
      });
      setResult(response.data);
      setError("");
      onGranted(response.data.provider, response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="provider-profile-editor" data-testid="provider-portal-access-panel">
      <div className="section-heading">
        <div>
          <h2>Provider portal access</h2>
          <p>
            {provider.first_name} {provider.last_name} · current status: {provider.portal_status}
          </p>
        </div>
        <button
          aria-label="Close portal access panel"
          className="projection-icon-button"
          data-testid="close-portal-access-panel-button"
          type="button"
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      {error && (
        <p className="form-message error-message" data-testid="portal-access-error">
          {error}
        </p>
      )}
      <form className="provider-portal-access-form" data-testid="portal-access-form" onSubmit={submit}>
        <label>
          Provider sign-in email
          <input
            data-testid="portal-access-email-input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label className="checkbox-field">
          <input
            checked={sendInvite}
            data-testid="portal-access-send-invite-checkbox"
            type="checkbox"
            onChange={(event) => setSendInvite(event.target.checked)}
          />
          Email set-password invite
        </label>
        <label className="checkbox-field">
          <input
            checked={issueTemp}
            data-testid="portal-access-temp-password-checkbox"
            type="checkbox"
            onChange={(event) => setIssueTemp(event.target.checked)}
          />
          Issue temporary password
        </label>
        <button
          className="button button-primary"
          data-testid="grant-portal-access-button"
          disabled={isSaving || (!sendInvite && !issueTemp)}
        >
          {sendInvite ? <Send size={16} /> : <KeyRound size={16} />} Enable access
        </button>
      </form>
      {result && (
        <div className="provider-portal-access-result" data-testid="portal-access-result">
          <p className="form-message success-message" data-testid="portal-access-success">
            Portal access enabled for {result.provider.email}.
            {result.invite_email_sent ? " Invitation email sent." : ""}
          </p>
          {result.invite_email_error && (
            <p className="form-message error-message" data-testid="portal-access-email-error">
              Invitation email could not be delivered ({result.invite_email_error}). Share the
              invite link below directly with the provider.
            </p>
          )}
          {result.invite_link && (
            <CopyValue label="Invite link" testId="portal-access-invite-link" value={result.invite_link} />
          )}
          {result.temporary_password && (
            <>
              <CopyValue
                label="Temporary password"
                testId="portal-access-temp-password"
                value={result.temporary_password}
              />
              <small className="secret-warning">
                Shown once. Share it securely with the provider; it is not emailed.
              </small>
            </>
          )}
        </div>
      )}
    </section>
  );
};
